import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DateTimeInput from '../../../../components/inputs/DateTimeInput';
import dayjs, { Dayjs } from 'dayjs';

interface BatchEditFormProps {
  onApplyToAll: (batchValues: BatchValues) => void;
}

export interface BatchValues {
  weightT?: number;
  lengthM?: number;
  fePrice?: number;
  loadingEarliestDate?: string;
  loadingLatestDate?: string;
  unloadingEarliestDate?: string;
  unloadingLatestDate?: string;
}

const BatchEditForm: React.FC<BatchEditFormProps> = ({ onApplyToAll }) => {
  const { t } = useTranslation();
  
  const [batchValues, setBatchValues] = useState<BatchValues>({});

  const handleFieldChange = (field: keyof BatchValues, value: any) => {
    setBatchValues(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApply = () => {
    // Only apply defined values
    const valuesToApply: BatchValues = {};
    Object.entries(batchValues).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        valuesToApply[key as keyof BatchValues] = value;
      }
    });
    
    if (Object.keys(valuesToApply).length > 0) {
      onApplyToAll(valuesToApply);
      // Reset form
      setBatchValues({});
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('Batch Edit')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('Fill in values to apply them to all offers at once')}
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {/* Weight, Length, Price */}
        <TextField
          fullWidth
          sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' } }}
          label={t('Client Price')}
          type="number"
          value={batchValues.fePrice ?? ''}
          onChange={(e) => handleFieldChange('fePrice', e.target.value ? parseFloat(e.target.value) : undefined)}
          size="small"
          placeholder={t('Leave empty to skip')}
        />
        <TextField
          fullWidth
          sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' } }}
          label={t('Weight (t)')}
          type="number"
          value={batchValues.weightT ?? ''}
          onChange={(e) => handleFieldChange('weightT', e.target.value ? parseFloat(e.target.value) : undefined)}
          size="small"
          placeholder={t('Leave empty to skip')}
        />
        <TextField
          fullWidth
          sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' } }}
          label={t('Length (m)')}
          type="number"
          value={batchValues.lengthM ?? ''}
          onChange={(e) => handleFieldChange('lengthM', e.target.value ? parseFloat(e.target.value) : undefined)}
          size="small"
          placeholder={t('Leave empty to skip')}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2}}>
        {/* Loading Place Dates */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <DateTimeInput
            type="datetime"
            sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
            label={t('Earliest Loading Date')}
            value={batchValues.loadingEarliestDate ? dayjs(batchValues.loadingEarliestDate) : null}
            onChange={(date: Dayjs | null) => handleFieldChange('loadingEarliestDate', date ? date.toISOString() : undefined)}
            size="small"
            fullWidth
          />
          <DateTimeInput
            type="datetime"
            sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
            label={t('Latest Loading Date')}
            value={batchValues.loadingLatestDate ? dayjs(batchValues.loadingLatestDate) : null}
            onChange={(date: Dayjs | null) => handleFieldChange('loadingLatestDate', date ? date.toISOString() : undefined)}
            size="small"
            fullWidth
          />
        </Box>

        {/* Unloading Place Dates */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <DateTimeInput
            type="datetime"
            sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
            label={t('Earliest Unloading Date')}
            value={batchValues.unloadingEarliestDate ? dayjs(batchValues.unloadingEarliestDate) : null}
            onChange={(date: Dayjs | null) => handleFieldChange('unloadingEarliestDate', date ? date.toISOString() : undefined)}
            size="small"
            fullWidth
          />
          <DateTimeInput
            type="datetime"
            sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 8px)' } }}
            label={t('Latest Unloading Date')}
            value={batchValues.unloadingLatestDate ? dayjs(batchValues.unloadingLatestDate) : null}
            onChange={(date: Dayjs | null) => handleFieldChange('unloadingLatestDate', date ? date.toISOString() : undefined)}
            size="small"
            fullWidth
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={Object.values(batchValues).every(v => v === undefined || v === '')}
        >
          {t('Apply to All Offers')}
        </Button>
      </Box>
    </Box>
  );
};

export default BatchEditForm;
